import React, { useState, useEffect } from 'react'
import { Button, Typography } from '~/components/_atoms'
import ChatHeader from '../ChatHeader'
import { useTranslation } from '~/hooks/useTranslation'
import { useCandidateChat } from '../CandidateChatContext'
import { getEffectiveExitUrl } from '../utils/getEffectiveExitUrl'

/**
 * MissedCall component - displays when a call to a coach was missed
 * Similar structure to ChatEnded but focused on missed call scenario
 */
const MissedCall = ({
  candidateCoachChatSurveyPath: _candidateCoachChatSurveyPath,
  afterChatPath,
  deloitteUser: _deloitteUser = false,
}) => {
  const { t } = useTranslation('coach_chat')
  const { getDarkModeClasses, connectionState } = useCandidateChat()
  const [missedCallData, setMissedCallData] = useState(null)

  // Load missed call data from localStorage on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    let conversationId = urlParams.get('conversation_id')

    // If no conversation ID in URL, try to get it from lastChatId in localStorage
    if (!conversationId) {
      try {
        conversationId = localStorage.getItem('candidate-lastChatId')
      } catch (error) {
        console.warn('Error reading lastChatId from localStorage:', error)
      }
    }

    if (conversationId) {
      try {
        const storedData = localStorage.getItem(
          `candidate-missed-call-${conversationId}`,
        )
        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData)
            // Validate conversation ID matches if present
            if (
              !parsedData.conversationId ||
              parsedData.conversationId === conversationId
            ) {
              setMissedCallData(parsedData)
            } else {
              console.warn(
                'Conversation ID mismatch in stored missed call data, ignoring',
              )
            }
          } catch (error) {
            console.error('Error parsing stored missed call data:', error)
          }
        } else {
          console.warn(
            'No missed call data found in localStorage for conversation:',
            conversationId,
          )
        }
      } catch (error) {
        console.error(
          'Error reading missed call data from localStorage:',
          error,
        )
      }
    } else {
      console.warn('No conversation ID available for loading missed call data')
    }
  }, [])

  // Handle close — footer Close and header X both exit to afterChatPath
  const handleCloseClick = () => {
    window.top.location.href = getEffectiveExitUrl(afterChatPath)
  }

  // Get coach name for display
  const getCoachName = () => {
    if (missedCallData?.coach?.displayName) {
      return missedCallData.coach.displayName
    }
    if (connectionState.selectedCoach?.displayName) {
      return connectionState.selectedCoach.displayName
    }
    return t('call_missed.coach_unknown', { defaultValue: 'the coach' })
  }

  // Get call type display text
  const getCallTypeText = () => {
    const callType = missedCallData?.callType || 'text'
    switch (callType) {
      case 'video':
        return t('call_missed.video_call', { defaultValue: 'video call' })
      case 'audio':
        return t('call_missed.audio_call', { defaultValue: 'audio call' })
      default:
        return t('call_missed.text_chat', { defaultValue: 'chat' })
    }
  }

  return (
    <div
      className={`missed-call-container grid h-full w-full grid-rows-[auto_1fr_auto] border ${getDarkModeClasses('border-cod-gray-200 bg-white', 'border-gray-600 bg-gray-800')}`}
    >
      {/* Header */}
      <div className="missed-call-header row-start-1">
        <ChatHeader
          selectedCoach={missedCallData?.coach || connectionState.selectedCoach}
          chatMode={missedCallData?.callType || 'text'}
          chatStatus="call_missed"
          showControls={false}
          showCloseButton
          onEndChat={handleCloseClick}
        />
      </div>

      {/* Main Content */}
      <div
        className={`missed-call-content row-start-2 flex flex-col items-center justify-center p-8 text-center ${getDarkModeClasses('bg-white', 'bg-gray-800')}`}
      >
        {/* Missed Call Icon */}
        <div className="mb-6">
          <div
            className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${getDarkModeClasses('bg-red-100', 'bg-red-900')}`}
          >
            <svg
              className={`h-10 w-10 ${getDarkModeClasses('text-red-600', 'text-red-300')}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 8l-4-4-4 4m8 0l-4 4-4-4"
              />
            </svg>
          </div>
        </div>

        {/* Primary heading */}
        <Typography
          variant="title2"
          className={`mb-3 ${getDarkModeClasses('text-cod-gray-900', 'text-white')}`}
        >
          {t('call_missed.title', { defaultValue: 'Call Missed' })}
        </Typography>

        {/* Supporting detail */}
        <Typography
          variant="body"
          className={`mb-2 max-w-md ${getDarkModeClasses('text-cod-gray-700', 'text-gray-300')}`}
        >
          {t('call_missed.message', {
            coachName: getCoachName(),
            callType: getCallTypeText(),
            defaultValue:
              'Your {{callType}} to {{coachName}} was not answered.',
          })}
        </Typography>

        {/* Secondary guidance */}
        <Typography
          variant="small"
          className={`mb-8 max-w-lg ${getDarkModeClasses('text-cod-gray-600', 'text-gray-400')}`}
        >
          {t('call_missed.help_text', {
            coachName: getCoachName(),
            defaultValue: 'Please choose another coach or try again later.',
          })}
        </Typography>
      </div>

      {/* Action Buttons */}
      <div
        className={`missed-call-actions border-t p-6 ${getDarkModeClasses('border-cod-gray-200 bg-white', 'border-gray-600 bg-gray-800')}`}
      >
        {/* Close — solid green (design-system secondary) matches schedule-meeting CTA */}
        <Button
          variant="solid"
          color="secondary"
          size="md"
          fullWidth
          onClick={handleCloseClick}
          className="h-10 rounded-2xl px-6"
        >
          {t('call_missed.close', { defaultValue: 'Close' })}
        </Button>
      </div>
    </div>
  )
}

export default MissedCall
